import { motion } from "framer-motion";
import { ArrowRight, Play, Star } from "lucide-react";
import { TRUST_BADGES, HERO_IMAGE } from "../../data/site";

const stagger = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

export default function Hero() {
  return (
    <section
      id="top"
      className="relative pt-28 md:pt-32 pb-20 md:pb-28 bg-[#FAFAFA] overflow-hidden"
      data-testid="hero-section"
    >
      {/* Dot grid */}
      <div className="absolute inset-0 bg-grid-pattern opacity-60 pointer-events-none" />
      {/* Violet glow */}
      <div className="absolute right-0 top-0 w-[600px] h-[600px] glow-orb bg-electric/15 pointer-events-none -translate-y-1/4 translate-x-1/4" />
      <div className="absolute left-0 bottom-0 w-[400px] h-[400px] glow-orb bg-electric/8 pointer-events-none translate-y-1/4 -translate-x-1/4" />

      <div className="container relative grid lg:grid-cols-12 gap-10 items-center">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          className="lg:col-span-7"
        >
          {/* Eyebrow pill */}
          <motion.div
            variants={item}
            className="inline-flex items-center gap-2 rounded-full border border-electric/25 bg-electric/8 px-4 py-2 mb-6"
            data-testid="hero-eyebrow"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-electric animate-blink" />
            <span className="text-overline text-electric">
              Full-Stack Digital Marketing · AI-First · Results-Driven
            </span>
          </motion.div>

          <motion.h1
            variants={item}
            className="font-display text-[2.6rem] sm:text-5xl lg:text-[4.25rem] leading-[1.05] font-bold text-charcoal text-balance"
            data-testid="hero-headline"
          >
            The{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-gradient">Digital Marketing</span>
            </span>{" "}
            Agency that turns clicks into&nbsp;customers.
          </motion.h1>

          <motion.p
            variants={item}
            className="mt-6 text-lg md:text-xl text-zinc-500 max-w-2xl leading-relaxed"
            data-testid="hero-subheadline"
          >
            Lumotis is a full-stack digital marketing agency — combining AI Search
            Optimization, SEO, Local SEO, Paid Ads, Social Media, and Email
            Marketing under one roof. We grow revenue for ecommerce brands, real
            estate agencies, salons, and 15+ other industries.
          </motion.p>

          <motion.div
            variants={item}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <a
              href="#audit"
              data-testid="hero-cta-audit"
              aria-label="Get a free marketing audit"
              className="inline-flex items-center gap-2 bg-electric text-white px-6 py-3.5 font-semibold rounded-xl hover:bg-electric-500 hover:-translate-y-0.5 transition-all shadow-lg shadow-electric/30"
            >
              Get Free Marketing Audit <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#case-studies"
              data-testid="hero-cta-cases"
              aria-label="See Lumotis case studies"
              className="inline-flex items-center gap-2 bg-white border border-zinc-200 text-charcoal px-6 py-3.5 font-semibold rounded-xl hover:border-electric/40 hover:bg-electric/5 transition-colors shadow-sm"
            >
              <Play className="w-4 h-4" /> See Results
            </a>
          </motion.div>

          {/* Trust badges */}
          <motion.dl
            variants={item}
            className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-px bg-zinc-100 rounded-2xl overflow-hidden border border-zinc-100"
            data-testid="hero-trust-badges"
          >
            {TRUST_BADGES.map((b) => (
              <div
                key={b.label}
                className="bg-white px-5 py-4"
              >
                <dt className="font-display text-2xl md:text-3xl font-bold text-charcoal tracking-tight">
                  {b.value}
                </dt>
                <dd className="mt-1 text-xs text-zinc-400 font-medium">
                  {b.label}
                </dd>
              </div>
            ))}
          </motion.dl>
        </motion.div>

        {/* Hero visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] w-full max-w-md mx-auto">
            {/* Glow ring behind image */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-electric/20 to-electric-700/10 blur-2xl scale-105" />
            <div className="relative w-full h-full overflow-hidden rounded-3xl bg-zinc-50 border border-zinc-200/60 shadow-2xl shadow-electric/10">
              <img
                src={HERO_IMAGE}
                alt="Digital marketing growth dashboard showing Lumotis client results"
                width="600"
                height="750"
                loading="eager"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Floating rating card */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="absolute -left-6 top-10 hidden md:block glass-card p-3 rounded-2xl"
              data-testid="hero-floating-rating"
            >
              <div className="flex items-center gap-1 text-electric">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-electric" />
                ))}
              </div>
              <div className="font-display text-2xl font-bold text-charcoal mt-1">
                4.9 / 5
              </div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 mt-0.5">
                Avg. client rating
              </div>
            </motion.div>

            {/* Floating ROAS card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="absolute -right-6 bottom-12 hidden md:block bg-charcoal text-white p-3 rounded-2xl shadow-xl shadow-charcoal/30"
              data-testid="hero-floating-rank"
            >
              <div className="text-[10px] uppercase tracking-widest text-electric-400">
                Avg. ROAS
              </div>
              <div className="font-display text-2xl font-bold mt-1">4.8×</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 mt-0.5">
                Paid Ads · Ecommerce
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
